import React, { useContext } from 'react';
import { MenuItem, ListItemIcon, ListItemText, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useApprovePracticalEvidenceMutation } from '../../../services/SkillsManagement';
import { useDispatch } from 'react-redux';
import * as actions from '../../../Reducers/Actions';
import { SnackBarContext } from '../../../Context/SnackBarContextProvider';

/**
 * ApprovePracticalEvidence
 * Renders a MenuItem that approves a practical evidence.
 *
 * Props:
 * - evidenceId: string | number (required)
 * - reviewComment?: string
 * - onApproved?: (payload) => void  // receives mutation response data
 * - onError?: (error) => void
 */
export default function ApprovePracticalEvidence({ evidenceId, reviewComment, onApproved, onError, closeParentMenu }) {
  const [approve, { isLoading }] = useApprovePracticalEvidenceMutation();
  const dispatch = useDispatch();
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);

  const handleApprove = async () => {
    if (!evidenceId || isLoading) return;
    try {
      const payload = await approve({
        evidenceId: String(evidenceId),
        body: reviewComment ? { review_comment: reviewComment } : {},
      }).unwrap();
      // Optimistically update redux list
      dispatch({ type: actions.APPROVE_PRACTICAL_EVIDENCE, payload: (payload?.data || { evidenceId }) });
      // Snackbar success
      setInfoAlert({ severity: 'success', message: 'Evidence approved successfully' });
      setOpenSnackBar(true);
      // Optionally notify parent
      if (onApproved) onApproved(payload);
      // Close parent menu if provided
      if (typeof closeParentMenu === 'function') closeParentMenu();
    } catch (err) {
      // Snackbar error
      const msg = err?.data?.message || err?.message || 'Failed to approve evidence';
      setInfoAlert({ severity: 'error', message: msg });
      setOpenSnackBar(true);
      if (onError) onError(err);
    }
  };

  return (
    <MenuItem onClick={handleApprove} disabled={!evidenceId || isLoading}>
      <ListItemIcon>
        {isLoading ? <CircularProgress size={18} /> : <CheckCircleOutlineIcon fontSize="small" color="success" />}
      </ListItemIcon>
      <ListItemText primary="Approve" />
    </MenuItem>
  );
}

